import { apiClient } from './api';
import { API_CONFIG, getHotelSlugFromDomain } from '../config/api';
import { ApiResponse } from '../types/api';

export interface TenantInfo {
  id: string;
  name: string;
  brandDetails: {
    brand: {
      name: string;
      fonts: {
        primary: string;
      };
      colors: {
        cta: string;
        text: {
          light: string;
          primary: string;
          secondary: string;
        };
        primary: string;
        gradient: {
          end: string;
          start: string;
          middle: string;
        };
        secondary: string;
      };
      shadows: {
        light: string;
        medium: string;
      };
    };
    aiAssistant: {
      name: string;
      avatar: {
        size: string;
        border: string;
        shadow: string;
        fallback: {
          letter: string;
          backgroundColor: string;
        };
        gradient: {
          end: string;
          start: string;
        };
      };
      logoUrl?: string;
      nameStyle: {
        color: string;
        fontSize: string;
        fontWeight: string;
      };
      timestamp: {
        color: string;
        fontSize: string;
      };
    };
    accessibility: {
      focusOutline: string;
      minTouchTarget: string;
      focusOutlineWidth: string;
      focusOutlineOffset: string;
    };
    userInterface: {
      topBar: {
        text: string;
        background: string;
        borderBottom: string;
      };
      inputBox: {
        active: {
          text: string;
          border: string;
          shadow: string;
          background: string;
        };
        inactive: {
          text: string;
          border: string;
          background: string;
          placeholder: string;
        };
      };
      sendButton: {
        text: string;
        hover: {
          border: string;
          shadow: string;
          background: string;
        };
        border: string;
        shadow: string;
        disabled: {
          border: string;
          background: string;
          text: string;
        };
        background: string;
      };
      userMessage: {
        text: string;
        border: string;
        shadow: string;
        background: string;
      };
    };
    aiResponseSystem: {
      avatar: {
        size: string;
        image: {
          width: string;
          height: string;
          objectFit: string;
        };
        border: string;
        shadow: string;
        borderRadius: string;
      };
      contentTypes: {
        text: {
          color: string;
          fontSize: string;
          lineHeight: string;
        };
        chips: {
          text: string;
          hover: {
            border: string;
            shadow: string;
            background: string;
          };
          border: string;
          padding: string;
          fontSize: string;
          minHeight: string;
          background: string;
          borderRadius: string;
        };
        image: {
          shadow: string;
          caption: {
            text: string;
            padding: string;
            fontSize: string;
            borderTop: string;
            background: string;
          };
          borderRadius: string;
        };
        details: {
          title: {
            text: string;
            padding: string;
            fontSize: string;
            background: string;
            fontWeight: string;
            borderBottom: string;
          };
          border: string;
          content: {
            text: string;
            padding: string;
            fontSize: string;
            background: string;
            lineHeight: string;
          };
          background: string;
          borderRadius: string;
        };
        feature: {
          icon: string;
          text: string;
          border: string;
          padding: string;
          background: string;
          borderLeft: string;
          borderRadius: string;
        };
        success: {
          icon: string;
          text: string;
          border: string;
          padding: string;
          background: string;
          borderLeft: string;
          borderRadius: string;
        };
        warning: {
          icon: string;
          text: string;
          border: string;
          padding: string;
          background: string;
          borderLeft: string;
          borderRadius: string;
        };
        highlight: {
          icon: string;
          text: string;
          border: string;
          padding: string;
          background: string;
          borderLeft: string;
          borderRadius: string;
        };
      };
      responseCard: {
        border: string;
        shadow: string;
        background: string;
        borderRadius: string;
        padding?: {
          default: string;
          large: string;
        };
      };
    };
    transactionalUI?: any; // Full transactionalUI theme structure from API
  };
  contactInfo?: {
    email: string;
    phone: string;
  };
  email?: string;
  phone?: string;
  guestJourney?: 'prospect' | 'pre-stay' | 'in-stay' | 'post-stay';
  // AI Assistant fields from API response
  ai_assistant_name?: string;
  ai_assistant_logo_url?: string;
  // Keep legacy brandSettings for backward compatibility
  brandSettings?: {
    primaryColor: string;
    secondaryColor: string;
    theme: string;
    logo?: string | null;
  };
  logo_url?: string;
  faviconURL?: string;
  pageTitle?: {
    auth: string;
    chat: string;
  };
  pageDescription?: {
    auth: string;
    chat: string;
  };
  // Additional hotel-specific fields for structured data
  slug?: string;
  description?: string;
  images?: string[];
  starRating?: number;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  };
  amenities?: string[];
  keywords?: string;
  supported_languages?: Array<{ code: string; name: string }>;
  default_language?: string;
}

export interface HotelTimezoneResponse {
  hotelId: string;
  hotelName: string;
  timezone: string;
  timezoneName: string;
  currentTime: string;
  currentDate: string; // Format: "Monday, December 29, 2025"
  dateTime: string; // Format: "2025-12-29 12:26:53"
  timestamp: number;
}

export interface UserJourneyStageResponse {
  journey_stage: 'prospect' | 'pre-stay' | 'in-stay' | 'post-stay';
  check_in_date?: string;
  check_out_date?: string;
  days_until_arrival?: number;
  days_since_checkout?: number;
  show_discover_menu: boolean;
  preferred_language?: {
    code: string;
    name?: string;
  };
}

export interface DiscoverMenuItem {
  icon: string;
  label: string;
  description: string;
  guestUtterance: string;
}

export interface DiscoverCategory {
  key: string;
  label: string;
  items: DiscoverMenuItem[];
}

export interface DiscoverMenuResponse {
  label: string;
  description: string;
  categories: DiscoverCategory[];
  journeyStages: string[];
}

export interface HotelService {
  getTenantInfo(): Promise<TenantInfo>;
  getHotelTimezone(hotelId: string): Promise<HotelTimezoneResponse>;
  getUserJourneyStage(hotelId: string): Promise<UserJourneyStageResponse | null>;
  getDiscoverMenu(hotelId: string, journeyStage: string): Promise<DiscoverMenuResponse | null>;
}

class HotelServiceImpl implements HotelService {
  /**
   * Get tenant information from the API
   */
  async getTenantInfo(): Promise<TenantInfo> {
    try {
      const hotelSlug = getHotelSlugFromDomain();
      console.log(`🏨 Fetching tenant info for hotel: ${hotelSlug}`);

      const endpoint = API_CONFIG.hotels.endpoints.getTenantInfo(hotelSlug);
      console.log('🔗 Hotel service endpoint:', endpoint);

      const response = await apiClient.get<TenantInfo>(endpoint);
      if (response.data) {
        console.log('✅ Tenant info fetched successfully:', response.data);

        // // Update AI assistant info from API response if available
        if (response.data.ai_assistant_name) {
          response.data.brandDetails.aiAssistant.name = response.data.ai_assistant_name;
        }
        if (response.data.ai_assistant_logo_url) {
          response.data.brandDetails.aiAssistant.logoUrl = response.data.ai_assistant_logo_url;
        }

        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to fetch tenant info');
      }
    } catch (error) {
      console.error('❌ Failed to fetch tenant info:', error);
      return null;
    }
  }

  /**
   * Get hotel timezone and current date
   */
  async getHotelTimezone(hotelId: string): Promise<HotelTimezoneResponse> {
    try {
      console.log(`🕐 Fetching hotel timezone for hotel: ${hotelId}`);

      const endpoint = `/tenants/${hotelId}/get-hotel-timezone`;
      const response = await apiClient.get<HotelTimezoneResponse>(endpoint);

      if (response.data) {
        console.log('✅ Hotel timezone fetched successfully:', response.data);
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to fetch hotel timezone');
      }
    } catch (error) {
      console.error('❌ Failed to fetch hotel timezone:', error);
      throw error;
    }
  }

  /**
   * Get user journey stage
   */
  async getUserJourneyStage(hotelId: string): Promise<UserJourneyStageResponse | null> {
    try {
      console.log(`🚀 Fetching user journey stage for hotel: ${hotelId}`);

      const endpoint = `/tenants/${hotelId}/get-user-journey-stage`;
      const response = await apiClient.get<UserJourneyStageResponse>(endpoint);

      if (response.data) {
        console.log('✅ User journey stage fetched successfully:', response.data);
        return response.data;
      } else {
        console.warn('⚠️ No journey stage data returned');
        return null;
      }
    } catch (error) {
      console.error('❌ Failed to fetch user journey stage:', error);
      // Return default values if API fails
      return {
        journey_stage: 'prospect',
        show_discover_menu: false
      };
    }
  }

  /**
   * Get discover menu for user's journey stage
   */
  async getDiscoverMenu(hotelId: string, journeyStage: string): Promise<DiscoverMenuResponse | null> {
    try {
      console.log(`🔍 Fetching discover menu for hotel: ${hotelId}, journey stage: ${journeyStage}`);

      const endpoint = `/tenants/${hotelId}/discover-menu/${journeyStage}`;
      const response = await apiClient.get<DiscoverMenuResponse>(endpoint);

      if (response.data) {
        console.log('✅ Discover menu fetched successfully:', response.data);
        return response.data;
      } else {
        console.warn('⚠️ No discover menu data returned');
        return null;
      }
    } catch (error) {
      console.error('❌ Failed to fetch discover menu:', error);
      return null;
    }
  }

  async setUserPreferredLanguage(
    hotelId: string,
    guestEmail: string,
    languageCode: string,
  ): Promise<void> {
    const endpoint = `/tenants/${hotelId}/set-user-preferred-language`;
    await apiClient.post(endpoint, { guestEmail, languageCode });
  }
}

// Export singleton instance
export const hotelService = new HotelServiceImpl();
export default hotelService;